const classes = {
  alternateDrop: 'disclosure__modal--alternate-drop',
  alternateVerticalDrop: 'disclosure__modal--above',
};

/**
 * @class Disclosure
 * @classdesc Manages the opening and closing of disclosure elements.
 */
export default class Disclosure extends Core.Component {
  constructor(el, context) {
    super(el, context);

    this.disclosureToggle = el.querySelector('[data-disclosure-toggle]');
    this.disclosureModal = el.querySelector('[data-disclosure-modal]');
    this.disclosureOptions = el.querySelectorAll('[data-disclosure-option]');
    this.disclosureClose = el.querySelector('[data-disclosure-close]');

    this.isOpen = false;

    const input = document.createElement('input');
    input.name = this.data.inputName;
    input.value = this.data.inputValue;
    input.type = 'hidden';
    input.dataset.disclosureInput = true;

    this.el.appendChild(input);

    this.disclosureAnimation = Core.Animations.transition({ el: this.disclosureModal, state: 'closed' });

    this.disclosureInput = input;

    // Set up the click events for the toggle button
    this.events.register(this.disclosureToggle, 'click', e => {
      if (this.isOpen) {
        this._hideList();
        return;
      }

      const ariaExpanded = e.currentTarget.getAttribute('aria-expanded') === 'true';

      e.currentTarget.setAttribute('aria-expanded', !ariaExpanded);

      this.disclosureModal.classList.remove(classes.alternateDrop);
      this.disclosureModal.classList.remove(classes.alternateVerticalDrop);

      this.isOpen = true;

      this.disclosureAnimation.animateTo('open', {
        onStart: ({ el: disclosureModal }) => {
          const { right, bottom } = disclosureModal.getBoundingClientRect();
          const { innerWidth, innerHeight } = window;
          const gutter = 30;
          if ((right + gutter) > innerWidth) {
            disclosureModal.classList.add(classes.alternateDrop);
          }

          if ((bottom + gutter) > innerHeight) {
            disclosureModal.classList.add(classes.alternateVerticalDrop);
          }
        },
      });

      const siteOverlay = Core.Components.getComponent('site-overlay');

      if (siteOverlay && Core.Breakpoints.max('S')) {
        siteOverlay.show();
      }
    });

    // Set up the click event handlers for the disclosure options
    this.disclosureOptions.forEach(option => {
      this.events.register(option, 'click', e => {
        e.preventDefault();
        this.disclosureInput.value = e.currentTarget.dataset.value;

        if (!this.data.alwaysShowHeading) {
          this.disclosureToggle.innerHTML = e.currentTarget.innerHTML;
        }

        this.disclosureOptions.forEach(item => {
          if (item === option) {
            item.classList.add('disclosure__link--selected');
            item.setAttribute('aria-current', true);
          } else {
            item.classList.remove('disclosure__link--selected');
            item.setAttribute('aria-current', false);
          }
        });

        this.el.dispatchEvent(new Event('input', { bubbles: true }));

        // Give it a little bit of a delay after a new choice is selected before closing
        // so that users can see that their option was selected.
        setTimeout(() => this._hideList(), 300);
      });
    });

    this.events.register(this.disclosureClose, 'click', e => {
      e.preventDefault();
      this._hideList();
    });

    // Setup events for keyboard navigation
    this.events.register(this.disclosureToggle, 'focusout', e => {
      const disclosureLostFocus = !this.el.contains(e.relatedTarget);

      if (disclosureLostFocus) {
        this._hideList();
      }
    });

    this.events.register(this.disclosureModal, 'focusout', e => {
      const childInFocus = e.currentTarget.contains(e.relatedTarget);

      if (this.isOpen && !childInFocus) {
        this._hideList();
      }
    });

    this.events.register(window, 'keyup', e => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (this.el.contains(document.activeElement)) {
          this.disclosureToggle.focus();
        }

        this._hideList();
      }
    });

    this.events.register(document.body, 'click', e => {
      const isOption = this.el.contains(e.target);

      if (this.isOpen && !isOption) {
        this._hideList();
      }
    });
  }

  /**
   * Gets the current value of the input
   *
   * @returns Value of the input
   */
  getValue() {
    return this.disclosureInput.value;
  }

  /**
   * Closes the disclosure list
   * @private
   */
  _hideList() {
    this.disclosureAnimation.animateTo('closed').then(() => {
      this.isOpen = false;
      this.disclosureToggle.setAttribute('aria-expanded', false);
    });

    const siteOverlay = Core.Components.getComponent('site-overlay');

    if (siteOverlay && Core.Breakpoints.max('S')) {
      siteOverlay.hide();
    }
  }
}
